/* eslint-disable no-console */
import Vue from "vue";
import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";
import App from "./App.vue";
import VueGtm from "vue-gtm";
import router from "./router";

import Ionic from "@ionic/vue";
import "./styles/main.scss";
import store from "./store";
import { parseValueToCurrency } from "./utils/number";
import { HeaderColor } from "@ionic-native/header-color";
import { Plugins, StatusBarStyle } from "@capacitor/core";
import { stringToFullHex } from "./utils/string";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { setEnvironment } from "./utils/environment";
import * as GmapVue from "gmap-vue";
// import "capacitor-gangl-tools";
// import EdgeCheck from "vue-edge-check";
import { addIcons } from "ionicons";
import {
    arrowBack,
    add,
    informationCircleOutline,
    trash,
    arrowDropdown,
    arrowDropup,
    share,
    copy,
    shareAlt,
    close,
    pin,
    heart,
    heartEmpty,
    fingerPrint,
    create,
    power,
    eye,
    eyeOff,
    mic,
    micOff,
    reverseCamera,
    desktop,
    closeCircleOutline,
    hammer,
    download,
} from "ionicons/icons";

addIcons({
    "ios-arrow-back": arrowBack.ios,
    "ios-add": add.ios,
    "ios-information-circle-outline": informationCircleOutline.ios,
    "ios-trash": trash.ios,
    "ios-arrow-dropdown": arrowDropdown.ios,
    "ios-arrow-dropup": arrowDropup.ios,
    "ios-share": share.ios,
    "ios-copy": copy.ios,
    "ios-share-alt": shareAlt.ios,
    "ios-close": close.ios,
    "ios-pin": pin.ios,
    "ios-heart": heart.ios,
    "ios-heart-empty": heartEmpty.ios,
    "ios-finger-print": fingerPrint.ios,
    "ios-create": create.ios,
    "ios-power": power.ios,
    "ios-eye": eye.ios,
    "ios-eye-off": eyeOff.ios,
    "ios-mic": mic.ios,
    "ios-mic-off": micOff.ios,
    "ios-reverse-camera": reverseCamera.ios,
    "ios-desktop": desktop.ios,
    "ios-close-circle-outline": closeCircleOutline.ios,
    "ios-hammer": hammer.ios,
    "ios-download": download.ios,

    "md-arrow-back": arrowBack.md,
    "md-add": add.md,
    "md-information-circle-outline": informationCircleOutline.md,
    "md-trash": trash.md,
    "md-arrow-dropdown": arrowDropdown.md,
    "md-arrow-dropup": arrowDropup.md,
    "md-share": share.md,
    "md-copy": copy.md,
    "md-share-alt": shareAlt.md,
    "md-close": close.md,
    "md-pin": pin.md,
    "md-heart": heart.md,
    "md-heart-empty": heartEmpty.md,
    "md-finger-print": fingerPrint.md,
    "md-create": create.md,
    "md-power": power.md,
    "md-eye": eye.md,
    "md-eye-off": eyeOff.md,
    "md-mic": mic.md,
    "md-mic-off": micOff.md,
    "md-reverse-camera": reverseCamera.md,
    "md-desktop": desktop.md,
    "md-close-circle-outline": closeCircleOutline.md,
    "md-hammer": hammer.md,
    "md-download": download.md,
});

const { StatusBar, Storage, GanglTools } = Plugins;

if (process.env.NODE_ENV === "production") {
    Sentry.init({
        dsn: "https://0f0063d68b8044f080f7396cb54c1bef@sentry.io/1818625",
        integrations: [new Integrations.Vue({ Vue, attachProps: true })],
    });
}

// Darkmode
// Android fix for enabling dark mode, see native implementation in MainActivity.java
// Use matchMedia to check the user preference
const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
if (window.navigator.userAgent.includes("AndroidDarkMode")) {
    // Apply dark theme or set theme css variables to dark
    toggleDarkTheme(true);
} else {
    toggleDarkTheme(prefersDark.matches);
}
// Listen for changes to the prefers-color-scheme media query
prefersDark.addListener((mediaQuery) => toggleDarkTheme(mediaQuery.matches));
// Add or remove the "dark" class based on if the media query matches
function toggleDarkTheme(shouldAdd) {
    Storage.get({ key: "darkmode" }).then(({ value: val }) => {
        if (val === null) {
            document.body.classList.toggle("dark", shouldAdd);
        } else if (val === "true") {
            document.body.classList.toggle("dark", true);
        }
        if (Capacitor.isNative) {
            if (val === null) {
                StatusBar.setStyle({
                    style: shouldAdd
                        ? StatusBarStyle.Dark
                        : StatusBarStyle.Light,
                });
            } else if (val === "true") {
                StatusBar.setStyle({
                    style: StatusBarStyle.Dark,
                });
            } else {
                StatusBar.setStyle({
                    style: StatusBarStyle.Light,
                });
            }
            const color = stringToFullHex(
                getComputedStyle(document.body).getPropertyValue(
                    "--ion-background-color"
                )
            );
            if (Capacitor.platform === "android") {
                StatusBar.setBackgroundColor({ color: color });
            }
        }
    });
}

// GanglTools.isRunningTestEnvironment().then((result) => {
//     console.log(result);
// });
// console.log(Plugins);
// const ganglTools = new GanglTools();
// ganglTools.isRunningTestEnvironment.then(({ value: value }) => {
//     if (value) {
//         setEnvironment("aemtest2");
//     }
// });

if (Capacitor.isNative) {
    GanglTools.isRunningTestEnvironment().then(({ value: value }) => {
        if (value === true) {
            setEnvironment("");
            store.commit("setNativeEnvironment", "development");

            console.defaultLog = console.log.bind(console);
            console.log = function () {
                store.commit("addDevelopmentLog", {
                    type: "log",
                    datetime: Date().toLocaleString(),
                    value: Array.from(arguments),
                });
                console.defaultLog.apply(console, arguments);
            };
            console.defaultError = console.error.bind(console);
            console.error = function () {
                store.commit("addDevelopmentLog", {
                    type: "error",
                    datetime: Date().toLocaleString(),
                    value: Array.from(arguments),
                });
                console.defaultError.apply(console, arguments);
            };
            console.defaultWarn = console.warn.bind(console);
            console.warn = function () {
                store.commit("addDevelopmentLog", {
                    type: "warn",
                    datetime: Date().toLocaleString(),
                    value: Array.from(arguments),
                });
                console.defaultWarn.apply(console, arguments);
            };
            console.defaultDebug = console.debug.bind(console);
            console.debug = function () {
                store.commit("addDevelopmentLog", {
                    type: "debug",
                    datetime: Date().toLocaleString(),
                    value: Array.from(arguments),
                });
                console.defaultDebug.apply(console, arguments);
            };
        }
    });
    if (Capacitor.platform === "android") {
        const headerColor = HeaderColor;
        headerColor.tint("#ed2a24");
    } else if (Capacitor.platform === "ios") {
        // Capacitor.Plugins.IosSwipeBack.enable();
        wkWebView.injectCookie(
            ".klickmal.at/",
            () => {
                // console.log("cookie injected: " + success);
            },
            () => {
                // console.log("cookie not injected: " + error);
            }
        );
    }
}

Vue.use(Ionic);
Vue.use(VueGtm, {
    id: "GTM-M9Z3M5G", // Your GTM single container ID or array of container ids ['GTM-xxxxxxx', 'GTM-yyyyyyy']
    // queryParams: {
    //   // Add url query string when load gtm.js with GTM ID (optional)
    //   gtm_auth: "AB7cDEf3GHIjkl-MnOP8qr",
    //   gtm_preview: "env-4",
    //   gtm_cookies_win: "x"
    // },
    enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: false, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    // vueRouter: router // Pass the router instance to automatically sync with router (optional)
    // ignoredViews: ["homepage"] // If router, you can exclude some routes name (case insensitive) (optional)
});
Vue.use(GmapVue, {
    load: {
        // TODO: replace with Wüstenrot Key
        key: "AIzaSyDGDoylfbikKXsej2VRTgAoiKA8KJ8DSjQ",
        libraries: "places", // This is required if you use the Autocomplete plugin
        // OR: libraries: 'places,drawing'
        // OR: libraries: 'places,drawing,visualization'
        // (as you require)

        //// If you want to set the version, you can do so:
        // v: '3.26',
    },

    //// If you intend to programmatically custom event listener code
    //// (e.g. `this.$refs.gmap.$on('zoom_changed', someFunc)`)
    //// instead of going through Vue templates (e.g. `<GmapMap @zoom_changed="someFunc">`)
    //// you might need to turn this on.
    // autobindAllEvents: false,

    //// If you want to manually install components, e.g.
    //// import {GmapMarker} from 'gmap-vue/src/components/marker'
    //// Vue.component('GmapMarker', GmapMarker)
    //// then set installComponents to 'false'.
    //// If you want to automatically install all the components this property must be set to 'true':
    installComponents: true,
});
// Vue.use(EdgeCheck);
Vue.config.productionTip = false;

Vue.filter("currency", function (value, sign = "€", decimals = 2) {
    return parseValueToCurrency(value, sign, decimals);
});

Vue.filter("float", function (value) {
    return parseValueToCurrency(value, "");
});

Vue.filter("percent", function (value) {
    let result = "";
    let intValue = parseInt(value);
    if (!isNaN(intValue)) {
        result = intValue + "%";
    }
    return result;
});

// eslint-disable-next-line
Vue.prototype.$log = console.log;

new Vue({
    router,
    store,
    render: (h) => h(App),
}).$mount("#app");

defineCustomElements(window);
